import React, { useState } from 'react';
import { winteamImportAPI } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Upload, CheckCircle, Loader2, ChevronRight, UserMinus, ShieldCheck, Mail, Download, HelpCircle } from 'lucide-react';
import { FileDrop, StatCard, HeaderReport, FlagTable, ReasonList } from '../components/import/ImportPrimitives';

export default function WinTeamEmployeeImportTab() {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('additions');
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [ack, setAck] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    if (!file) { toast.error('Choose the Employee Master export'); return; }
    setParsing(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mode', mode);
      const { data } = await winteamImportAPI.employeePreview(fd);
      setPreview(data);
      setAck(false);
      toast.success('Preview ready — nothing has been written yet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally {
      setParsing(false);
    }
  };

  const confirm = async () => {
    setApplying(true);
    try {
      const { data } = await winteamImportAPI.employeeConfirm({
        preview_id: preview.preview_id,
        acknowledge_deactivations: ack,
      });
      setResult(data);
      setPreview(null);
      setFile(null);
      toast.success(`${data.created_count} created · ${data.updated_count} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setApplying(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const { data } = await winteamImportAPI.employeeChangesCsv(preview.preview_id);
      const url = window.URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `winteam-changes-${preview.preview_id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not download the change file');
    }
  };

  const t = preview?.totals || {};
  const warn = preview?.deactivation_warning;

  return (
    <div className="space-y-6" data-testid="winteam-employee-import-tab">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" /> WinTeam Employee Master Import
          </CardTitle>
          <CardDescription>
            HR Report → Employee Master (235 columns, <span className="font-mono text-xs">.csv</span> or{' '}
            <span className="font-mono text-xs">.xlsx</span>). WinTeam is the system of record for HR, payroll
            and tax fields — they are overwritten on every import. Evaluation settings, uniform sizes and asset
            records are never touched. Employees are matched on EmployeeNumber and clients are resolved from
            PrimaryJob.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4 space-y-2" data-testid="winteam-employee-mode">
            <p className="text-sm font-medium text-fg">What is in this file?</p>
            <ModeOption
              value="additions"
              current={mode}
              onPick={() => { setMode('additions'); setPreview(null); setResult(null); }}
              title="Just some of the roster — add and update only"
              body="For a few new hires or corrections. Everyone missing from the file is left exactly as they are. Nobody is deactivated."
              testId="winteam-mode-additions"
            />
            <ModeOption
              value="full_roster"
              current={mode}
              onPick={() => { setMode('full_roster'); setPreview(null); setResult(null); }}
              title="The complete roster — full sync"
              body="A genuine full WinTeam export. Anyone active in TriCorps 360 who is not in the file will be marked inactive, so terminated staff close out automatically."
              testId="winteam-mode-full"
            />
          </div>

          <FileDrop
            label="Employee Master export"
            hint="Click to choose .csv or .xlsx"
            accept=".csv,.xlsx"
            file={file}
            onFile={(f) => { setFile(f); setPreview(null); setResult(null); }}
            testId="winteam-employee-file-drop"
          />
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={runPreview} disabled={parsing || !file} data-testid="winteam-employee-preview-btn">
              {parsing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Parsing…</> : 'Run Dry-Run Preview'}
            </Button>
            {preview && <span className="text-xs text-slate-500">Preview cached for 30 minutes.</span>}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Alert className="border-emerald-300 bg-emerald-50" data-testid="winteam-employee-apply-result">
          <CheckCircle className="h-4 w-4 text-emerald-600" />
          <AlertDescription>
            <div className="font-semibold text-emerald-800">Employee Master import complete</div>
            <div className="text-sm text-emerald-700 mt-1">
              {result.created_count} created · {result.updated_count} updated · {result.deactivated_count} deactivated ·{' '}
              {result.tax_records_written} tax records · {result.employees_with_clients} with client coverage
            </div>
          </AlertDescription>
        </Alert>
      )}

      {preview && (
        <>
          {preview.mode === 'additions' ? (
            <Alert className="border-emerald-300 bg-emerald-50" data-testid="winteam-mode-banner-additions">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <AlertDescription className="text-sm text-emerald-800">
                <span className="font-semibold">Add and update only.</span> No one will be deactivated by this
                import — everybody missing from the file stays exactly as they are.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-amber-300 bg-amber-50" data-testid="winteam-mode-banner-full">
              <UserMinus className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-sm text-amber-800">
                <span className="font-semibold">Full roster sync.</span> {t.will_deactivate || 0} active
                employees are not in this file and will be marked inactive. Check the Deactivate tab before you
                confirm.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Rows parsed" value={t.parsed_rows} testId="stat-parsed" />
            <StatCard label="Will create" value={t.will_create} tone="text-emerald-600" testId="stat-create" />
            <StatCard label="Will update" value={t.will_update} tone="text-blue-600" testId="stat-update" />
            <StatCard label="Will deactivate" value={t.will_deactivate} tone="text-rose-600" testId="stat-deactivate" />
            <StatCard label="Needs review" value={t.needs_review} tone="text-amber-600" testId="stat-review" />
          </div>

          <HeaderReport report={preview.header_report} />

          <div className="flex items-center justify-between gap-3 rounded-md border border-line bg-surface-sunken px-3 py-2">
            <span className="text-sm text-fg-muted">
              {t.field_changes || 0} field change{(t.field_changes || 0) === 1 ? '' : 's'} across all updated employees
              {(t.email_changes || 0) > 0 && <> · <span className="font-medium text-fg">{t.email_changes} email change{t.email_changes === 1 ? '' : 's'}</span></>}
            </span>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!(t.field_changes)} data-testid="wt-emp-download-changes-csv">
              <Download className="w-4 h-4 mr-1.5" /> Download change CSV
            </Button>
          </div>

          <Tabs defaultValue="create" className="space-y-4">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="create" data-testid="wt-emp-tab-create">
                Create <Badge className="ml-2 bg-emerald-100 text-emerald-800">{t.will_create || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="update" data-testid="wt-emp-tab-update">
                Update <Badge className="ml-2 bg-blue-100 text-blue-800">{t.will_update || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="wt-emp-tab-email">
                Email changes <Badge className="ml-2 bg-violet-100 text-violet-800">{t.email_changes || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="deactivate" data-testid="wt-emp-tab-deactivate">
                Deactivate <Badge className="ml-2 bg-rose-100 text-rose-800">{t.will_deactivate || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="decision" data-testid="wt-emp-tab-decision">
                Needs decision <Badge className="ml-2 bg-orange-100 text-orange-800">{t.absent_needs_decision || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="review" data-testid="wt-emp-tab-review">
                Needs review <Badge className="ml-2 bg-amber-100 text-amber-800">{t.needs_review || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="flags" data-testid="wt-emp-tab-flags">
                Unused columns with data <Badge className="ml-2 bg-slate-200 text-slate-700">{t.flagged_columns || 0}</Badge>
              </TabsTrigger>
              <TabsTrigger value="protected" data-testid="wt-emp-tab-protected">
                Kept from TriCorps <Badge className="ml-2 bg-indigo-100 text-indigo-800">{t.protected_fields || 0}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="create"><EmployeeRows rows={preview.will_create} /></TabsContent>
            <TabsContent value="update"><EmployeeRows rows={preview.will_update} /></TabsContent>
            <TabsContent value="email"><EmailChanges rows={preview.email_changes} /></TabsContent>
            <TabsContent value="deactivate"><DeactivateRows rows={preview.will_deactivate} /></TabsContent>
            <TabsContent value="decision"><DecisionRows rows={preview.absent_needs_decision} /></TabsContent>
            <TabsContent value="review">
              <ReasonList
                rows={preview.needs_review}
                emptyText="Everything resolved cleanly — no manual review needed."
                testId="wt-emp-review-list"
              />
            </TabsContent>
            <TabsContent value="flags"><FlagTable flags={preview.flags} /></TabsContent>
            <TabsContent value="protected"><ProtectedTable rows={preview.protected} /></TabsContent>
          </Tabs>

          <div className="flex items-center justify-end gap-3">
            {warn?.requires_ack && (
              <label className="flex items-center gap-2 mr-auto text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ack}
                  onChange={(e) => setAck(e.target.checked)}
                  data-testid="wt-emp-ack-deactivations"
                />
                This export is the full roster — deactivate {warn.count} of {warn.active_total} WinTeam-sourced employees
              </label>
            )}
            <Button variant="outline" onClick={() => setPreview(null)} data-testid="wt-emp-discard-btn">Discard preview</Button>
            <Button onClick={confirm} disabled={applying || (warn?.requires_ack && !ack)} data-testid="winteam-employee-confirm-btn">
              {applying ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Applying…</> : <>Confirm import <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function ModeOption({ value, current, onPick, title, body, testId }) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={onPick}
      data-testid={testId}
      className={`w-full text-left rounded-md border p-3 transition-colors ${
        active ? 'border-brand bg-brand/5' : 'border-line hover:bg-surface-sunken'}`}
    >
      <span className="flex items-start gap-2">
        <span className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${
          active ? 'border-brand bg-brand' : 'border-line'}`} />
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-fg">{title}</span>
          <span className="block text-sm text-fg-muted mt-0.5">{body}</span>
        </span>
      </span>
    </button>
  );
}

function EmployeeRows({ rows }) {
  if (!rows?.length) return <p className="text-sm text-slate-400 py-6 text-center">No rows in this bucket.</p>;
  return (
    <Card><CardContent className="p-0">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Emp #</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Primary job</TableHead>
          <TableHead>Client added</TableHead>
          <TableHead>Hired</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.slice(0, 500).map((r) => (
            <TableRow key={`${r.employee_number}-${r.row_index}`} data-testid={`wt-emp-row-${r.employee_number}`}>
              <TableCell className="font-mono text-xs">{r.employee_number}</TableCell>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell>
                <Badge variant={r.status === 'Active' ? 'secondary' : 'outline'}>{r.status || '—'}</Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{r.primary_job || '—'}</TableCell>
              <TableCell className="text-sm">{r.client_added || '—'}</TableCell>
              <TableCell className="text-sm">{r.hire_date || '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 500 && (
        <p className="p-3 text-xs text-slate-500">Showing the first 500 of {rows.length} rows.</p>
      )}
    </CardContent></Card>
  );
}

const PROTECT_REASON = {
  locally_edited: 'Edited here',
  blank_in_file: 'Blank in file',
  company_email: 'Company email',
  linked_account: 'Linked login',
};

function ProtectedTable({ rows }) {
  if (!rows?.length) {
    return (
      <p className="text-sm text-slate-400 py-6 text-center" data-testid="wt-emp-protected-empty">
        Nothing to keep — this file does not disagree with anything edited here.
      </p>
    );
  }
  return (
    <Card><CardContent className="p-0">
      <p className="p-4 pb-2 text-sm text-slate-600">
        These values are kept from TriCorps 360. A field is held back when a person edited it
        here, when the file cell is blank, when the current address is on the company domain
        (and the file would move it off it), or when the employee is linked to a login.
      </p>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Emp #</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Field</TableHead>
          <TableHead>Keeping</TableHead>
          <TableHead>Ignoring from file</TableHead>
          <TableHead>Why</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.slice(0, 500).map((r, i) => (
            <TableRow key={`${r.employee_number}-${r.field}-${i}`} data-testid={`wt-emp-protected-${r.employee_number}-${r.field}`}>
              <TableCell className="font-mono text-xs">{r.employee_number}</TableCell>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="font-mono text-xs">{r.field}</TableCell>
              <TableCell className="text-sm">{String(r.kept_value ?? '—')}</TableCell>
              <TableCell className="text-sm text-slate-500">
                {r.file_value === '' || r.file_value === null ? '(blank)' : String(r.file_value)}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className="text-xs">
                  {PROTECT_REASON[r.reason] || 'Kept'}
                </Badge>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 500 && (
        <p className="p-3 text-xs text-slate-500">Showing the first 500 of {rows.length} values.</p>
      )}
    </CardContent></Card>
  );
}

function DeactivateRows({ rows }) {
  if (!rows?.length) return <p className="text-sm text-slate-400 py-6 text-center">Nobody will be deactivated by this import.</p>;
  return (
    <Card><CardContent className="p-4 space-y-2">
      <p className="text-sm text-slate-600 mb-2">
        These WinTeam-sourced employees are not in the file and will be marked inactive. Their records,
        assets and evaluation history are kept.
      </p>
      {rows.slice(0, 500).map((r) => (
        <div key={r.id} className="flex items-center gap-2 p-2 bg-rose-50 border border-rose-200 rounded-md text-sm" data-testid={`wt-emp-deactivate-${r.employee_number}`}>
          <UserMinus className="w-4 h-4 text-rose-500" />
          <span className="font-mono text-xs">{r.employee_number}</span>
          <span className="font-medium">{r.name}</span>
          <Badge variant="outline" className="ml-auto text-xs">From WinTeam import</Badge>
        </div>
      ))}
      {rows.length > 500 && (
        <p className="text-xs text-slate-500">Showing the first 500 of {rows.length} employees.</p>
      )}
    </CardContent></Card>
  );
}

function DecisionRows({ rows }) {
  if (!rows?.length) {
    return (
      <p className="text-sm text-slate-400 py-6 text-center" data-testid="wt-emp-decision-empty">
        No TriCorps 360-created employees are missing from this file.
      </p>
    );
  }
  return (
    <Card><CardContent className="p-4 space-y-2">
      <Alert className="mb-3 border-orange-200 bg-orange-50">
        <HelpCircle className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-orange-800">
          These employees were created in TriCorps 360 and are absent from the file. They are
          <strong> not deactivated automatically</strong> — review each one and change their status by hand if needed.
        </AlertDescription>
      </Alert>
      {rows.slice(0, 500).map((r) => (
        <div key={r.id} className="flex items-center gap-2 p-2 bg-orange-50 border border-orange-200 rounded-md text-sm" data-testid={`wt-emp-decision-${r.employee_number}`}>
          <HelpCircle className="w-4 h-4 text-orange-500" />
          <span className="font-mono text-xs">{r.employee_number}</span>
          <span className="font-medium">{r.name}</span>
          <Badge variant="outline" className="ml-auto text-xs">Created in TriCorps 360</Badge>
        </div>
      ))}
      {rows.length > 500 && (
        <p className="text-xs text-slate-500">Showing the first 500 of {rows.length} employees.</p>
      )}
    </CardContent></Card>
  );
}

function EmailChanges({ rows }) {
  if (!rows?.length) {
    return (
      <p className="text-sm text-slate-400 py-6 text-center" data-testid="wt-emp-email-empty">
        No email addresses will change. Company-domain and linked-login addresses are protected.
      </p>
    );
  }
  return (
    <Card><CardContent className="p-0">
      <p className="p-4 pb-2 text-sm text-slate-600 flex items-center gap-2">
        <Mail className="w-4 h-4 text-violet-500" />
        Every email address the import will change. Company-domain and linked-login addresses are
        held back and appear under “Kept from TriCorps”.
      </p>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Emp #</TableHead>
          <TableHead>Name</TableHead>
          <TableHead>Current</TableHead>
          <TableHead>New (from file)</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.slice(0, 500).map((r, i) => (
            <TableRow key={`${r.employee_number}-${i}`} data-testid={`wt-emp-email-${r.employee_number}`}>
              <TableCell className="font-mono text-xs">{r.employee_number}</TableCell>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell className="text-sm text-slate-500">{r.current || '(blank)'}</TableCell>
              <TableCell className="text-sm font-medium">{r.new || '(blank)'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {rows.length > 500 && (
        <p className="p-3 text-xs text-slate-500">Showing the first 500 of {rows.length} changes.</p>
      )}
    </CardContent></Card>
  );
}
